<template>
  <div class="new-base-range">
    <!--
      Native range input acting as a custom slider:
      - `:value` binds to `modelValue` (one-way; updates flow back out via `newOnInput`/`update:modelValue`)
      - `@input` emits the new numeric value on every drag
      - `:style` paints a filled/unfilled gradient track split at `isRangeValue`% to visualize progress
    -->
    <input
      :value="modelValue"
      class="new-base-range__input"
      :type="'range'"
      :max="max"
      :min="min"
      @input="newOnInput"
      :style="{
        background: `linear-gradient(to right, #1390e5cc ${isRangeValue}%, #d5d5d5 ${isRangeValue}%), var(--color-background-primary)`,
      }"
    />
  </div>
</template>

<script setup>
import { ref, computed, watch } from "vue";
/**
 * @prop {Number|String} modelValue - Current slider value, bound via `v-model` (accepts string or number, coerced internally).
 * @prop {Number} max - Maximum selectable value.
 * @prop {Number} min - Minimum selectable value.
 */
const props = defineProps({
  modelValue: {
    // Accept both number and string for compatibility; coerce internally
    type: [Number, String],
    default: 0,
  },
  max: {
    type: Number,
    default: 50,
  },
  min: {
    type: Number,
    default: 1,
  },
});
/** Computes `modelValue` as a percentage of `max`, used to position the filled portion of the track gradient. */
const isRangeValue = computed(() => {
  const current = Number(props.modelValue) || 0;
  const max = Number(props.max) || 1;
  return (current / max) * 100;
});

/**
 * Handles the native `input` event on the range slider: reads the new value
 * from the DOM element and emits it as a number via `update:modelValue`.
 * @emits update:modelValue - Fired with the new numeric slider value.
 * @param {Event} e - The native `input` event from the range input.
 */
const newOnInput = (e) => {
  const target = e.target;
  // Narrow target to HTMLInputElement to access .value safely
  const input = target && 'value' in target ? /** @type {HTMLInputElement} */ (target) : null;
  const raw = input?.value ?? '0';
  const value = Number(raw);
  // Emit numeric value for range input
  // @ts-ignore - Vue macro typing for emit in JS
  $emit('update:modelValue', Number.isNaN(value) ? 0 : value);
};
</script>

<style lang="scss" scoped>
.new-base-range {
  width: 100%;
  &__input {
    position: relative;
    appearance: none;
    -webkit-appearance: none;
    -moz-appearance: none;
    display: block;
    color: #1390e5cc;
    background: rgba(23, 23, 25, 0.3);
    border-radius: 3px;
    outline: none;
    width: 100%;
    height: 6px;

    &::-webkit-slider-runnable-track {
      -webkit-appearance: none;
      height: 8px;
    }

    &::-moz-track {
      -moz-appearance: none;
      height: 8px;
    }

    &::-ms-track {
      appearance: none;
      height: 8px;
    }

    &::-webkit-slider-thumb {
      -webkit-appearance: none;
      height: 20px;
      width: 20px;
      /* OLD: border: 2px solid #1390e5; */
      border: 2px solid var(--color-accent-primary);
      border-radius: 50%;
      cursor: grab;
      margin-top: -6px;
      background: var(--color-foreground-primary);
    }

    &::-moz-range-thumb {
      -moz-appearance: none;
      height: 20px;
      width: 20px;
      /* OLD: border: 2px solid #1390e5; */
      border: 2px solid var(--color-accent-primary);
      border-radius: 50%;
      cursor: grab;
      margin-top: -6px;
      background: var(--color-foreground-primary);
    }
    &::-ms-thumb {
      appearance: none;
      height: 20px;
      width: 20px;
      /* OLD: border: 2px solid #1390e5; */
      border: 2px solid var(--color-accent-primary);
      border-radius: 50%;
      cursor: grab;
      margin-top: -6px;
      background: var(--color-foreground-primary);
    }

    &:active::-webkit-slider-thumb {
      background-color: var(--color-foreground-primary);
      /* OLD: border: 2px solid #1390e5; */
      border: 2px solid var(--color-accent-primary);
      cursor: grabbing;
    }
  }
}
</style>
